// pages/Metas.js - Pagina de Metas Inteligentes
import React, { useState, useEffect } from 'react';
import { format, subMonths, addMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import './Metas.css';

const Metas = () => {
    const [mesSelecionado, setMesSelecionado] = useState(format(new Date(), 'yyyy-MM'));
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [calculando, setCalculando] = useState(false);
    const [modalAberto, setModalAberto] = useState(false);
    const [campoAjuste, setCampoAjuste] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    useEffect(() => {
        buscarMeta();
    }, [mesSelecionado]);

    const buscarMeta = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/metas/${mesSelecionado}`);
            const data = await res.json();
            
            if (data.success) {
                setMeta(data.meta);
            } else {
                setMeta(null);
            }
        } catch (error) {
            console.error('Erro ao buscar meta:', error);
            setMeta(null);
        } finally {
            setLoading(false);
        }
    };

    const calcularMetas = async () => {
        if (!window.confirm(`Calcular metas automaticamente para ${formatarMes(mesSelecionado)}?`)) {
            return;
        }

        setCalculando(true);
        try {
            const res = await fetch(`${API_URL}/api/metas/calcular/${mesSelecionado}`, {
                method: 'POST'
            });
            const data = await res.json();
            
            if (data.success) {
                alert('Metas calculadas com sucesso!');
                buscarMeta();
            } else {
                alert('Erro ao calcular metas: ' + data.error);
            }
        } catch (error) {
            console.error('Erro ao calcular metas:', error);
            alert('Erro ao calcular metas');
        } finally {
            setCalculando(false);
        }
    };

    const abrirModalAjuste = (campo) => {
        setCampoAjuste(campo);
        setModalAberto(true);
    };

    const mudarMes = (direcao) => {
        const [ano, mes] = mesSelecionado.split('-');
        const data = new Date(ano, mes - 1);
        const novaData = direcao === 'anterior' ? subMonths(data, 1) : addMonths(data, 1);
        setMesSelecionado(format(novaData, 'yyyy-MM'));
    };

    const formatarMes = (mesAno) => {
        const [ano, mes] = mesAno.split('-');
        const data = new Date(ano, mes - 1);
        return format(data, 'MMMM/yyyy', { locale: ptBR });
    };

    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor || 0);
    };

    const calcularProgresso = (realizado, meta) => {
        if (!meta || meta === 0) return 0;
        return Math.min((realizado / meta) * 100, 100);
    };

    const getStatusCor = (progresso) => {
        if (progresso >= 90) return 'verde';
        if (progresso >= 70) return 'amarelo';
        if (progresso >= 50) return 'laranja';
        return 'vermelho';
    };

    if (loading) {
        return (
            <div className="metas-loading">
                <div className="spinner"></div>
                <p>Carregando metas...</p>
            </div>
        );
    }

    if (!meta) {
        return (
            <div className="metas-container">
                <div className="metas-header">
                    <h1>🎯 Metas Inteligentes</h1>
                    <p className="mes-selecionado">
                        {formatarMes(mesSelecionado).charAt(0).toUpperCase() + 
                         formatarMes(mesSelecionado).slice(1)}
                    </p>
                </div>

                <div className="metas-vazio">
                    <div className="vazio-icon">📊</div>
                    <h2>Nenhuma meta calculada para este mes</h2>
                    <p>Clique no botao abaixo para calcular automaticamente as metas baseadas no seu historico.</p>
                    <button 
                        onClick={calcularMetas} 
                        className="btn-calcular"
                        disabled={calculando}
                    >
                        {calculando ? 'Calculando...' : 'Calcular Metas Automaticamente'}
                    </button>
                </div>

                <div className="mes-navegacao">
                    <button onClick={() => mudarMes('anterior')}>← Mes Anterior</button>
                    <button onClick={() => mudarMes('proximo')}>Proximo Mes →</button>
                </div>
            </div>
        );
    }

    const progressoFaturamento = calcularProgresso(meta.faturamento_realizado, meta.meta_faturamento);
    const progressoLucro = calcularProgresso(meta.lucro_realizado, meta.meta_lucro);
    const progressoMargem = calcularProgresso(meta.margem_realizada * 100, meta.meta_margem * 100);
    const progressoTicket = calcularProgresso(meta.ticket_medio_realizado, meta.meta_ticket_medio);
    const progressoOS = calcularProgresso(meta.os_fechadas_realizadas, meta.meta_os_fechadas);

    return (
        <div className="metas-container">
            <div className="metas-header">
                <div className="header-left">
                    <h1>🎯 Metas Inteligentes</h1>
                    <p className="mes-selecionado">
                        {formatarMes(mesSelecionado).charAt(0).toUpperCase() + 
                         formatarMes(mesSelecionado).slice(1)}
                    </p>
                    {meta.ajustada_manualmente && (
                        <span className="badge-ajustado">✏️ Ajustado Manualmente</span>
                    )}
                </div>

                <div className="header-right">
                    <div className="mes-navegacao">
                        <button onClick={() => mudarMes('anterior')}>← Anterior</button>
                        <button onClick={() => mudarMes('proximo')}>Proximo →</button>
                    </div>
                    <button 
                        onClick={calcularMetas} 
                        className="btn-recalcular"
                        disabled={calculando}
                    >
                        {calculando ? 'Calculando...' : 'Recalcular'}
                    </button>
                </div>
            </div>

            <div className="metas-grid">
                {/* CARD 1: FATURAMENTO */}
                <div className={`meta-card card-${getStatusCor(progressoFaturamento)}`}>
                    <div className="card-header">
                        <h3>💰 Faturamento</h3>
                        <button 
                            className="btn-ajustar"
                            onClick={() => abrirModalAjuste('faturamento')}
                        >
                            ✏️ Ajustar
                        </button>
                    </div>

                    <div className="card-valores">
                        <div className="valor-principal">
                            <span className="label">Meta:</span>
                            <span className="valor">{formatarMoeda(meta.meta_faturamento)}</span>
                        </div>
                        <div className="valor-secundario">
                            <span className="label">Realizado:</span>
                            <span className="valor">{formatarMoeda(meta.faturamento_realizado)}</span>
                        </div>
                    </div>

                    <div className="progresso-bar">
                        <div 
                            className="progresso-fill"
                            style={{ width: `${progressoFaturamento}%` }}
                        ></div>
                    </div>
                    <div className="progresso-texto">
                        {progressoFaturamento.toFixed(1)}% alcancado
                    </div>

                    {meta.calculo_detalhado?.faturamento && (
                        <div className="card-explicacao">
                            <p>{meta.calculo_detalhado.faturamento.explicacao}</p>
                        </div>
                    )}

                    <div className="card-status">
                        {meta.atingiu_faturamento ? (
                            <span className="status-atingido">✅ Meta atingida!</span>
                        ) : (
                            <span className="status-falta">
                                Faltam {formatarMoeda(meta.meta_faturamento - meta.faturamento_realizado)}
                            </span>
                        )}
                    </div>
                </div>

                {/* CARD 2: LUCRO */}
                <div className={`meta-card card-${getStatusCor(progressoLucro)}`}>
                    <div className="card-header">
                        <h3>📈 Lucro</h3>
                        <button 
                            className="btn-ajustar"
                            onClick={() => abrirModalAjuste('lucro')}
                        >
                            ✏️ Ajustar
                        </button>
                    </div>

                    <div className="card-valores">
                        <div className="valor-principal">
                            <span className="label">Meta:</span>
                            <span className="valor">{formatarMoeda(meta.meta_lucro)}</span>
                        </div>
                        <div className="valor-secundario">
                            <span className="label">Realizado:</span>
                            <span className="valor">{formatarMoeda(meta.lucro_realizado)}</span>
                        </div>
                    </div>

                    <div className="card-detalhes">
                        <small>Custos: {formatarMoeda(meta.custos_realizados)}</small>
                        <small>(Produtos: {formatarMoeda(meta.custo_produtos_realizado)} + Terceiros: {formatarMoeda(meta.custo_terceiros_realizado)})</small>
                    </div>

                    <div className="progresso-bar">
                        <div 
                            className="progresso-fill"
                            style={{ width: `${progressoLucro}%` }}
                        ></div>
                    </div>
                    <div className="progresso-texto">
                        {progressoLucro.toFixed(1)}% alcancado
                    </div>

                    <div className="card-status">
                        {meta.atingiu_lucro ? (
                            <span className="status-atingido">✅ Meta atingida!</span>
                        ) : meta.lucro_realizado < 0 ? (
                            <span className="status-critico">🔴 PREJUIZO - Urgente!</span>
                        ) : (
                            <span className="status-falta">
                                Faltam {formatarMoeda(meta.meta_lucro - meta.lucro_realizado)}
                            </span>
                        )}
                    </div>
                </div>

                {/* CARD 3: MARGEM */}
                <div className={`meta-card card-${getStatusCor(progressoMargem)}`}>
                    <div className="card-header">
                        <h3>📊 Margem</h3>
                        <button 
                            className="btn-ajustar"
                            onClick={() => abrirModalAjuste('margem')}
                        >
                            ✏️ Ajustar
                        </button>
                    </div>

                    <div className="card-valores">
                        <div className="valor-principal">
                            <span className="label">Meta:</span>
                            <span className="valor">{(meta.meta_margem * 100).toFixed(1)}%</span>
                        </div>
                        <div className="valor-secundario">
                            <span className="label">Atual:</span>
                            <span className="valor">{(meta.margem_realizada * 100).toFixed(1)}%</span>
                        </div>
                    </div>

                    <div className="progresso-bar">
                        <div 
                            className="progresso-fill"
                            style={{ width: `${progressoMargem}%` }}
                        ></div>
                    </div>
                    <div className="progresso-texto">
                        {progressoMargem.toFixed(1)}% alcancado
                    </div>

                    {meta.calculo_detalhado?.margem && (
                        <div className="card-explicacao">
                            <p>{meta.calculo_detalhado.margem.explicacao}</p>
                        </div>
                    )}
                </div>

                {/* CARD 4: TICKET MEDIO + OS */}
                <div className={`meta-card card-${getStatusCor(progressoOS)}`}>
                    <div className="card-header">
                        <h3>✅ Desempenho OS</h3>
                    </div>

                    <div className="card-valores">
                        <div className="valor-principal">
                            <span className="label">Meta OS:</span>
                            <span className="valor">{meta.meta_os_fechadas} OS</span>
                        </div>
                        <div className="valor-secundario">
                            <span className="label">Realizadas:</span>
                            <span className="valor">{meta.os_fechadas_realizadas} OS</span>
                        </div>
                    </div>

                    <div className="progresso-bar">
                        <div 
                            className="progresso-fill"
                            style={{ width: `${progressoOS}%` }}
                        ></div>
                    </div>
                    <div className="progresso-texto">
                        {progressoOS.toFixed(1)}% alcancado
                    </div>

                    <div className="card-detalhes">
                        <small>Ticket Medio: {formatarMoeda(meta.ticket_medio_realizado)}</small>
                        <small>Meta: {formatarMoeda(meta.meta_ticket_medio)}</small>
                    </div>

                    <div className="card-status">
                        {meta.atingiu_os_fechadas ? (
                            <span className="status-atingido">✅ Meta atingida!</span>
                        ) : (
                            <span className="status-falta">
                                Faltam {meta.meta_os_fechadas - meta.os_fechadas_realizadas} OS
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="metas-info">
                <div className="info-box">
                    <h4>🤖 Calculo Automatico</h4>
                    <p>
                        Metas calculadas em {new Date(meta.calculado_em).toLocaleDateString('pt-BR')} 
                        as {new Date(meta.calculado_em).toLocaleTimeString('pt-BR')}
                    </p>
                    <p className="info-detalhe">
                        Baseado em {meta.calculo_detalhado?.meses_analisados || 6} meses de historico
                    </p>
                </div>

                <div className="info-box">
                    <h4>📊 Ultima Atualizacao</h4>
                    <p>
                        {new Date(meta.atualizado_em).toLocaleDateString('pt-BR')} 
                        as {new Date(meta.atualizado_em).toLocaleTimeString('pt-BR')}
                    </p>
                    <p className="info-detalhe">
                        Valores atualizados em tempo real
                    </p>
                </div>
            </div>

            {modalAberto && (
                <ModalAjusteMeta 
                    meta={meta}
                    campo={campoAjuste}
                    onClose={() => setModalAberto(false)}
                    onSalvar={buscarMeta}
                />
            )}
        </div>
    );
};

const ModalAjusteMeta = ({ meta, campo, onClose, onSalvar }) => {
    const [novoValor, setNovoValor] = useState('');
    const [motivo, setMotivo] = useState('');
    const [salvando, setSalvando] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

    const getNomeCampo = () => {
        const nomes = {
            'faturamento': 'Faturamento',
            'lucro': 'Lucro',
            'margem': 'Margem'
        };
        return nomes[campo] || campo;
    };

    const getValorAtual = () => {
        const campos = {
            'faturamento': meta.meta_faturamento,
            'lucro': meta.meta_lucro,
            'margem': (meta.meta_margem * 100).toFixed(1)
        };
        return campos[campo];
    };

    const handleSalvar = async () => {
        if (!novoValor || !motivo) {
            alert('Preencha o novo valor e o motivo');
            return;
        }

        setSalvando(true);
        try {
            let valorFinal = parseFloat(novoValor);
            
            if (campo === 'margem') {
                valorFinal = valorFinal / 100;
            }

            const res = await fetch(`${API_URL}/api/metas/ajustar/${meta.mes_referencia}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    campo: `meta_${campo}`,
                    valor: valorFinal,
                    motivo,
                    usuario: 'Usuario'
                })
            });

            const data = await res.json();
            
            if (data.success) {
                alert('Meta ajustada com sucesso!');
                onSalvar();
                onClose();
            } else {
                alert('Erro ao ajustar meta: ' + data.error);
            }
        } catch (error) {
            console.error('Erro ao ajustar meta:', error);
            alert('Erro ao ajustar meta');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>✏️ Ajustar Meta de {getNomeCampo()}</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <div className="modal-body">
                    <div className="form-group">
                        <label>Valor Atual:</label>
                        <input 
                            type="text" 
                            value={getValorAtual()} 
                            disabled 
                        />
                    </div>

                    <div className="form-group">
                        <label>Novo Valor:</label>
                        <input 
                            type="number" 
                            value={novoValor}
                            onChange={(e) => setNovoValor(e.target.value)}
                            placeholder="Digite o novo valor"
                            step={campo === 'margem' ? '0.1' : '1'}
                        />
                    </div>

                    <div className="form-group">
                        <label>Motivo do Ajuste:</label>
                        <textarea 
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            placeholder="Por que esta ajustando esta meta?"
                            rows="3"
                        />
                    </div>

                    <div className="modal-info">
                        ℹ️ Este ajuste ficara registrado no historico
                    </div>
                </div>

                <div className="modal-footer">
                    <button onClick={onClose} className="btn-cancelar">
                        Cancelar
                    </button>
                    <button 
                        onClick={handleSalvar} 
                        className="btn-salvar"
                        disabled={salvando}
                    >
                        {salvando ? 'Salvando...' : 'Salvar Ajuste'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Metas;
